import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ProgressAction, ProgressGetter } from '@/store/progress/progress.module';
import { ProgressAttachmentSchema } from '@/store/progress/progress.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProjectAttachmentFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    projectId: {
      type: Number
    },
    progressId: {
      type: Number
    }
  },
  emits: [
    'hidden'
  ],
  setup: (props, context) => {
    const store = useStore();
    const attachmentForm = ref<InstanceType<typeof BaseModalComponent>>();
    const fileDialog = ref<HTMLInputElement>();

    // form
    interface Form {
      progressId?: number | null;
      attachmentTypeId: number;
      name?: string | null;
      file?: string | null;
    }
    const formData = reactive<Form>({
      progressId: props.progressId,
      attachmentTypeId: 1,
      name: '',
      file: ''
    });
    const validator = computed(() => ProgressAttachmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      file: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'file'))
    });
    const isValid = computed(() => !errors.name && !errors.file);
    const isLoading = computed<boolean>(() => store.getters[ProgressGetter.STATUS] === 'init' || store.getters[ProgressGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      Promise.resolve(formData.progressId = props.progressId)
        .then(() => attachmentForm.value?.show());
    };

    // hide modal
    const hide = () => {
      attachmentForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.attachmentTypeId = 1;
        formData.name = '';
        formData.file = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const files = fileDialog.value?.files as FileList;
        store.dispatch(ProgressAction.ADD_ATTACHMENTS, {
          projectId: props.projectId,
          progressId: formData.progressId,
          attachmentTypeId: formData.attachmentTypeId,
          names: [formData.name],
          attachments: files,
        }).then(() => hide());
      }
    };

    const fileDialogChange = () => {
      const files = fileDialog.value?.files as FileList;
      const file = files.item(0) as File;
      formData.file = file.name;
      unsubmit();
    };

    const handleHidden = () => {
      context.emit('hidden', props.progressId);
    };

    return {
      formData,
      attachmentForm,
      fileDialog,
      isLoading,
      errors,
      submited,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      fileDialogChange,
      handleHidden,
    };
  },
});
